import { FilterList as FilterIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import type { Filters } from './useHelpdeskData';

interface HelpdeskFiltersProps {
  activeTab: string;
  handleTabChange: (event: React.SyntheticEvent, newValue: string) => void;
  filters: Filters;
  handleFilterChange: (field: keyof Filters) => (event: SelectChangeEvent<string>) => void;
  clearFilters: () => void;
}

export const HelpdeskFilters: React.FC<HelpdeskFiltersProps> = ({
  activeTab,
  handleTabChange,
  filters,
  handleFilterChange,
  clearFilters,
}) => {
  const theme = useTheme();

  return (
    <Paper variant="outlined" sx={{ mb: 3, p: 2, border: 'none' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              fontSize: '1rem',
              fontWeight: 500,
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              mr: 1,
              py: 1,
              bgcolor: theme.palette.greyLight,
              color: 'text.primary',
              '&.Mui-selected': {
                bgcolor: 'background.paper',
                color: 'primary.main',
                fontWeight: 600,
                border: `1px solid ${theme.palette.divider}`,
                borderBottomColor: theme.palette.background.paper,
                marginBottom: '-1px',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab label="All Tickets" value="all" />
          <Tab label="Open" value="open" />
          <Tab label="Active" value="active" />
          <Tab label="Resolved" value="resolved" />
          <Tab label="Urgent" value="urgent" />
        </Tabs>
      </Box>

      <Grid container spacing={2} alignItems="center" sx={{ p: 2 }}>
        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter-select"
              value={filters.status}
              onChange={handleFilterChange('status')}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="waiting-response">Waiting Response</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              onChange={handleFilterChange('category')}
              label="Category"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="feature-request">Feature Request</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority}
              onChange={handleFilterChange('priority')}
              label="Priority"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={clearFilters}
            startIcon={<FilterIcon />}
            sx={{
              backgroundColor: theme.palette.bidlogiq.brick,
              color: theme.palette.common.white,
              fontSize: '1.0rem',
              '&:hover': { backgroundColor: theme.palette.bidlogiq.brick },
            }}
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};
